import {useQuery} from '@github-ui/react-query'
import {useMemo} from 'react'

import {repositorySavedViewEndpoints, useSavedViewEndpoints} from '../contexts/SavedViewEndpointsContext'
import {fetchSavedViews, savedViewPinsQueryKey, savedViewsQueryOptions} from '../queries'
import type {SavedView, SavedViewsResponse, SavedViewVisibility} from '../types'
import {viewVisibility} from '../utils/views-visibility'

const EMPTY_SAVED_VIEWS: SavedView[] = []

export interface UsePinnedSavedViewsResult {
  pinnedViews: SavedView[]
  pinnedCount: number | undefined
  pinnedCountsByVisibility: Record<SavedViewVisibility, number> | undefined
  isFetching: boolean
}

export function usePinnedSavedViews({
  owner,
  repo,
  enabled,
  initialData,
  initialPinnedViews,
}: {
  owner: string
  repo: string
  enabled: boolean
  initialData?: SavedViewsResponse
  initialPinnedViews?: SavedView[]
}): UsePinnedSavedViewsResult {
  const endpoints = useSavedViewEndpoints(owner, repo)
  const repositoryEndpoints = repositorySavedViewEndpoints(owner, repo)
  const usesCustomEndpoints = endpoints.listUrl !== repositoryEndpoints.listUrl
  const repositoryQuery = useQuery({
    ...savedViewsQueryOptions({owner, repo, listUrl: endpoints.listUrl, initialData}),
    enabled: enabled && !usesCustomEndpoints,
  })
  const pinnedViewsQuery = useQuery({
    queryKey: savedViewPinsQueryKey({owner, repo, listUrl: endpoints.listUrl}),
    queryFn: async ({signal}) => getPinnedViews(await fetchSavedViews({listUrl: endpoints.listUrl, signal})),
    initialData: initialPinnedViews,
    staleTime: initialPinnedViews ? Infinity : 0,
    enabled: enabled && usesCustomEndpoints,
  })
  const data = usesCustomEndpoints ? pinnedViewsQuery.data : repositoryQuery.data
  const savedViews = usesCustomEndpoints
    ? ((data as SavedView[] | undefined) ?? EMPTY_SAVED_VIEWS)
    : ((data as SavedViewsResponse | undefined)?.savedViews ?? EMPTY_SAVED_VIEWS)
  const allPinnedViews = useMemo(
    () => (usesCustomEndpoints ? savedViews : savedViews.filter(view => view.pinned)),
    [savedViews, usesCustomEndpoints],
  )
  const pinnedViews = useMemo(() => (enabled ? allPinnedViews : []), [allPinnedViews, enabled])
  const pinnedCountsByVisibility = useMemo(
    () =>
      data
        ? allPinnedViews.reduce<Record<SavedViewVisibility, number>>(
            (counts, view) => {
              counts[viewVisibility(view)] += 1
              return counts
            },
            {team: 0, personal: 0},
          )
        : undefined,
    [allPinnedViews, data],
  )
  const pinnedCount = pinnedCountsByVisibility
    ? pinnedCountsByVisibility.personal + pinnedCountsByVisibility.team
    : undefined

  return {
    pinnedViews,
    pinnedCount,
    pinnedCountsByVisibility,
    isFetching: usesCustomEndpoints ? pinnedViewsQuery.isFetching : repositoryQuery.isFetching,
  }
}

function getPinnedViews(response: SavedViewsResponse): SavedView[] {
  return response.savedViews
    .filter(view => view.pinned)
    .toSorted((left, right) => {
      const visibilityOrder = viewVisibility(left) === 'personal' ? -1 : 1
      if (viewVisibility(left) !== viewVisibility(right)) return visibilityOrder
      return left.pinPosition !== undefined && right.pinPosition !== undefined
        ? left.pinPosition - right.pinPosition
        : 0
    })
}
